import { useTranslatedChatSize, useTranslatedDefinitionSize, useTranslatedLanguage, useUserSettings } from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { FilterActions } from "@next-platform/client-common-spicy-ui";
import type { SearchResults } from "algoliasearch-helper";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClearRefinements, useInstantSearch, useRefinementList } from "react-instantsearch";
import { useDebouncedCallback } from "use-debounce";

import { CHAT_SIZE_CATEGORY_VALUE_SET } from "@/components/search/chatSizeCategoryValues";
import { DEFINITION_SIZE_CATEGORY_VALUE_SET } from "@/components/search/definitionSizeCategoryValues";
import getFacetRefinementsFromSearchState from "@/components/search/utils/getFacetRefinementsFromSearchState";
import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import generateChatSizeData from "@/utils/generateChatSizeData";
import generateDefinitionData from "@/utils/generateDefinitionData";
import { generateLanguageData } from "@/utils/generateLanguageData";

import CapabilityFtueModal from "../../FtueModal/CapabilityFtueModal";
import { FtuePreference } from "../../FtueModal/types";
import FilterPanelCharacterV2 from "../FiltersV2/FilterPanelCharacterV2";
import useFiltersV2Registry from "../FiltersV2/useFiltersV2Registry";
import useRegisterCharacterFilterSectionsV2 from "../FiltersV2/useRegisterCharacterFilterSectionsV2";
import { DisplayContext } from "../types";
import { CHAT_SIZE_CATEGORY, DEFINITION_SIZE_CATEGORY, TRANSLATED_LANGUAGES_CATEGORY, UPDATE_SETTINGS_DEBOUNCE_TIME } from "./constants";
import FilterBoxFeatures from "./FilterBoxFeatures";
import FilterPanelCharacterV1 from "./FilterPanelCharacterV1";
import skipWhileLoading from "./skipWhileLoading";
import type { FilterOperator, FilterPanelCharacterProps } from "./types";
import { UpdateOptionsCharacter } from "./types";
import useTagFilter from "./useTagFilter";

const logger = appLogger.withTag("FilterBoxCharacter");
const EMPTY_SELECTED_VALUES: string[] = [];

interface IFilterBoxCharacterProps {
  operator: FilterOperator;
  useStoreTagFilters: boolean;
  context: DisplayContext | undefined;
  results: SearchResults | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTagsDrawerVisible: boolean;
  setIsTagsDrawerVisible: (value: boolean) => void;
  isDesktop: boolean;
  isTagListMinimized: boolean;
}

const FilterBoxCharacter = ({
  results,
  isAuthenticated,
  isLoading,
  operator,
  useStoreTagFilters = true,
  context,
  isTagsDrawerVisible,
  setIsTagsDrawerVisible,
  isDesktop,
  isTagListMinimized,
}: IFilterBoxCharacterProps) => {
  const isFiltersV2 = useFiltersV2Registry() !== null;
  const { getTranslatedLanguage } = useTranslatedLanguage();
  const { getTranslatedDefinitionSize } = useTranslatedDefinitionSize();
  const { getTranslatedChatSize } = useTranslatedChatSize();
  const [stateFeaturesSlot, setStateFeaturesSlot] = useState<HTMLDivElement | null>(null);
  const {
    onUserSelectsLanguage,
    onUserSelectsDefinitions,
    onUserSelectsChatSize,
    selectedLanguages: selectedLanguagesStore,
    selectedDefinitions: selectedDefinitionsStore,
    selectedChatSizes: selectedChatSizesStore,
    setLastTimeFilteredCharacters,
  } = useUserSettings();

  const { setIndexUiState } = useInstantSearch();
  const handleAfterFeatureAction = useCallback(() => setLastTimeFilteredCharacters(Date.now()), [setLastTimeFilteredCharacters]);

  const { hasTagRefinements, tagRefinementCount, tagsListData, applyTagAction, tagsActionHandler, refineTag } = useTagFilter({
    operator,
    results,
    useStoreTagFilters,
    isAuthenticated,
    isLoading,
    onAfterTagAction: () => setLastTimeFilteredCharacters(Date.now()),
  });

  const { items: languageItems, refine: refineLanguage } = useRefinementList({
    attribute: TRANSLATED_LANGUAGES_CATEGORY,
    operator,
    limit: 100,
  });

  const { items: definitionItems, refine: refineDefinition } = useRefinementList({
    attribute: DEFINITION_SIZE_CATEGORY,
    operator: "or",
    limit: 100,
  });

  const { items: chatSizeItems, refine: refineChatSize } = useRefinementList({
    attribute: CHAT_SIZE_CATEGORY,
    operator: "or",
    limit: 100,
  });

  const { hasFeatureFlag: hasFilterDefinition } = useFeatureFlag(FEATURE_FLAGS.FILTER_DEFINITION);

  // useRef (not useState) so flags survive React 18 StrictMode's effect double-invocation
  const refIsLanguageSelectedInitially = useRef(false);
  const refIsDefinitionSelectedInitially = useRef(false);
  const refIsChatSizeSelectedInitially = useRef(false);

  const refIsResettingLanguagesRef = useRef(false);
  const refIsResettingDefinitionsRef = useRef(false);
  const refIsResettingChatSizesRef = useRef(false);

  const selectedLanguagesSearchClient = useMemo(
    () =>
      getFacetRefinementsFromSearchState({
        searchState: results?._state,
        attributeName: TRANSLATED_LANGUAGES_CATEGORY,
        facetOperator: operator,
      }),
    [results, operator],
  );

  const selectedDefinitionsSearchClient = useMemo(
    () =>
      getFacetRefinementsFromSearchState({
        searchState: results?._state,
        attributeName: DEFINITION_SIZE_CATEGORY,
        facetOperator: "or",
      }),
    [results],
  );

  const selectedChatSizesSearchClient = useMemo(
    () =>
      getFacetRefinementsFromSearchState({
        searchState: results?._state,
        attributeName: CHAT_SIZE_CATEGORY,
        facetOperator: "or",
      }),
    [results],
  );

  const { refine: clearLanguageRefinement } = useClearRefinements({
    includedAttributes: ["translated_languages"],
  });
  const { refine: clearDefinitionRefinement } = useClearRefinements({
    includedAttributes: [DEFINITION_SIZE_CATEGORY],
  });
  const { refine: clearChatSizeRefinement } = useClearRefinements({
    includedAttributes: [CHAT_SIZE_CATEGORY],
  });

  const selectedLanguages = useStoreTagFilters ? selectedLanguagesStore : EMPTY_SELECTED_VALUES;
  const selectedDefinitions = useStoreTagFilters ? selectedDefinitionsStore : EMPTY_SELECTED_VALUES;
  const selectedChatSizes = useStoreTagFilters ? selectedChatSizesStore : EMPTY_SELECTED_VALUES;

  const handleResetLanguages = useCallback(() => {
    refIsResettingLanguagesRef.current = true;
    void clearLanguageRefinement();
    if (isAuthenticated && useStoreTagFilters) {
      void onUserSelectsLanguage([]);
    }
  }, [clearLanguageRefinement, isAuthenticated, useStoreTagFilters, onUserSelectsLanguage]);

  const handleResetDefinition = useCallback(() => {
    refIsResettingDefinitionsRef.current = true;
    void clearDefinitionRefinement();
    if (isAuthenticated && useStoreTagFilters) {
      void onUserSelectsDefinitions([]);
    }
  }, [clearDefinitionRefinement, isAuthenticated, useStoreTagFilters, onUserSelectsDefinitions]);

  const handleResetChatSizes = useCallback(() => {
    refIsResettingChatSizesRef.current = true;
    void clearChatSizeRefinement();
    if (isAuthenticated && useStoreTagFilters) {
      void onUserSelectsChatSize([]);
    }
  }, [clearChatSizeRefinement, isAuthenticated, useStoreTagFilters, onUserSelectsChatSize]);

  useEffect(() => {
    const isSkippingLanguagesReapply = refIsResettingLanguagesRef.current;
    const isSkippingDefinitionsReapply = refIsResettingDefinitionsRef.current;
    const isSkippingChatSizesReapply = refIsResettingChatSizesRef.current;

    refIsResettingLanguagesRef.current = false;
    refIsResettingDefinitionsRef.current = false;
    refIsResettingChatSizesRef.current = false;

    if (!results) {
      return;
    }

    const updates: Record<string, string[]> = {};

    if (!isSkippingLanguagesReapply && !refIsLanguageSelectedInitially.current && selectedLanguages.length) {
      const validLanguages = selectedLanguages.filter((refinementValue) => !refinementValue.startsWith("-"));
      const toAdd = validLanguages.filter((refinementValue) => !selectedLanguagesSearchClient?.includes(refinementValue));
      if (toAdd.length) {
        updates[TRANSLATED_LANGUAGES_CATEGORY] = [...new Set([...(selectedLanguagesSearchClient ?? []), ...toAdd])];
      } else {
        refIsLanguageSelectedInitially.current = true;
      }
    }

    if (!isSkippingDefinitionsReapply && !refIsDefinitionSelectedInitially.current && selectedDefinitions.length) {
      const validDefinitions = selectedDefinitions.filter((refinementValue) => DEFINITION_SIZE_CATEGORY_VALUE_SET.has(refinementValue));
      const toAdd = validDefinitions.filter((refinementValue) => !selectedDefinitionsSearchClient?.includes(refinementValue));
      if (toAdd.length) {
        updates[DEFINITION_SIZE_CATEGORY] = [...new Set([...(selectedDefinitionsSearchClient ?? []), ...toAdd])];
      } else {
        refIsDefinitionSelectedInitially.current = true;
      }
    }

    if (!isSkippingChatSizesReapply && !refIsChatSizeSelectedInitially.current && selectedChatSizes.length) {
      const validChatSizes = selectedChatSizes.filter((refinementValue) => CHAT_SIZE_CATEGORY_VALUE_SET.has(refinementValue));
      const toAdd = validChatSizes.filter((refinementValue) => !selectedChatSizesSearchClient?.includes(refinementValue));
      if (toAdd.length) {
        updates[CHAT_SIZE_CATEGORY] = [...new Set([...(selectedChatSizesSearchClient ?? []), ...toAdd])];
      } else {
        refIsChatSizeSelectedInitially.current = true;
      }
    }

    if (Object.keys(updates).length > 0) {
      setIndexUiState((prev) => ({
        ...prev,
        refinementList: {
          ...prev.refinementList,
          ...updates,
        },
      }));

      if (updates[TRANSLATED_LANGUAGES_CATEGORY]) {
        refIsLanguageSelectedInitially.current = true;
      }
      if (updates[DEFINITION_SIZE_CATEGORY]) {
        refIsDefinitionSelectedInitially.current = true;
      }
      if (updates[CHAT_SIZE_CATEGORY]) {
        refIsChatSizeSelectedInitially.current = true;
      }
    }
  }, [
    results,
    selectedLanguages,
    selectedLanguagesSearchClient,
    selectedDefinitions,
    selectedDefinitionsSearchClient,
    selectedChatSizes,
    selectedChatSizesSearchClient,
    setIndexUiState,
  ]);

  const updateSettings = useDebouncedCallback(async (option: Exclude<UpdateOptionsCharacter, UpdateOptionsCharacter.TAG>) => {
    const fns = {
      language: onUserSelectsLanguage,
      definition: onUserSelectsDefinitions,
    };

    const filterData = {
      language: selectedLanguagesSearchClient ?? [],
      definition: selectedDefinitionsSearchClient ?? [],
    };

    if (!isAuthenticated) {
      return;
    }
    try {
      if (!useStoreTagFilters) {
        return;
      }
      await fns[option](filterData[option]);
    } catch (error) {
      const { message } = error as Error;

      logger.error("Error while updating filter settings:", message);
    }
  }, UPDATE_SETTINGS_DEBOUNCE_TIME);

  const applyLanguageAction = useCallback(
    (action: FilterActions, value?: string) => {
      switch (action) {
        case FilterActions.INCLUDE:
          refineLanguage(value ?? "");
          break;
        case FilterActions.RESET:
          if (!selectedLanguagesSearchClient?.length && !selectedLanguages.length) return;
          handleResetLanguages();
          setLastTimeFilteredCharacters(Date.now());
          return;
        default:
          break;
      }
      void updateSettings(UpdateOptionsCharacter.LANGUAGE);
      setLastTimeFilteredCharacters(Date.now());
    },
    [refineLanguage, selectedLanguagesSearchClient, selectedLanguages, handleResetLanguages, updateSettings, setLastTimeFilteredCharacters],
  );

  const languageActionHandler = useMemo(() => skipWhileLoading({ isLoading, actionHandler: applyLanguageAction }), [isLoading, applyLanguageAction]);

  const applyDefinitionAction = useCallback(
    (action: FilterActions, value?: string) => {
      switch (action) {
        case FilterActions.INCLUDE:
          refineDefinition(value ?? "");
          break;
        case FilterActions.RESET:
          if (!selectedDefinitionsSearchClient?.length && !selectedDefinitions.length) return;
          handleResetDefinition();
          setLastTimeFilteredCharacters(Date.now());
          return;
        default:
          break;
      }
      void updateSettings(UpdateOptionsCharacter.DEFINITION);
      setLastTimeFilteredCharacters(Date.now());
    },
    [refineDefinition, selectedDefinitionsSearchClient, selectedDefinitions, handleResetDefinition, updateSettings, setLastTimeFilteredCharacters],
  );

  const definitionActionHandler = useMemo(
    () => skipWhileLoading({ isLoading, actionHandler: applyDefinitionAction }),
    [isLoading, applyDefinitionAction],
  );

  const applyChatSizeAction = useCallback(
    (action: FilterActions, value?: string) => {
      switch (action) {
        case FilterActions.INCLUDE:
          refineChatSize(value ?? "");
          break;
        case FilterActions.RESET:
          if (!selectedChatSizesSearchClient?.length && !selectedChatSizes.length) return;
          handleResetChatSizes();
          setLastTimeFilteredCharacters(Date.now());
          return;
        default:
          break;
      }
      setLastTimeFilteredCharacters(Date.now());
    },
    [refineChatSize, selectedChatSizesSearchClient, selectedChatSizes, handleResetChatSizes, setLastTimeFilteredCharacters],
  );
  const chatSizeActionHandler = useMemo(() => skipWhileLoading({ isLoading, actionHandler: applyChatSizeAction }), [isLoading, applyChatSizeAction]);

  const languageListData = useMemo(
    () => generateLanguageData(languageItems, languageActionHandler, (label) => getTranslatedLanguage(label)),
    [languageItems, languageActionHandler, getTranslatedLanguage],
  );
  const definitionListData = useMemo(
    () => generateDefinitionData(definitionItems, definitionActionHandler, (label) => getTranslatedDefinitionSize(label)),
    [definitionItems, definitionActionHandler, getTranslatedDefinitionSize],
  );
  const chatSizeListData = useMemo(
    () => generateChatSizeData(chatSizeItems, chatSizeActionHandler, (sizeSource) => getTranslatedChatSize(sizeSource)),
    [chatSizeItems, chatSizeActionHandler, getTranslatedChatSize],
  );

  const filterPanelProps: FilterPanelCharacterProps = {
    context,
    isDesktop,
    isOpen: isTagsDrawerVisible,
    onOpenChange: setIsTagsDrawerVisible,
    setFeaturesSlot: setStateFeaturesSlot,
    hasFilterDefinition,
    hasTagRefinements,
    tagRefinementCount,
    languageRefinementCount: selectedLanguagesSearchClient?.length ?? 0,
    definitionRefinementCount: selectedDefinitionsSearchClient?.length ?? 0,
    chatSizeRefinementCount: selectedChatSizesSearchClient?.length ?? 0,
    tagsListData,
    tagsActionHandler,
    definitionListData,
    definitionActionHandler,
    languageListData,
    languageActionHandler,
    chatSizeListData,
    chatSizeActionHandler,
  };

  useRegisterCharacterFilterSectionsV2({
    ...filterPanelProps,
    tagsActionHandler: applyTagAction,
    languageActionHandler: applyLanguageAction,
    definitionActionHandler: applyDefinitionAction,
    chatSizeActionHandler: applyChatSizeAction,
  });

  const featuresFilter =
    context !== DisplayContext.MEMBER_SELECT ? (
      <CapabilityGate capability={SubCapabilityKey.DiscoverFilterFeatures}>
        <FilterBoxFeatures results={results} isLoading={isLoading} onAfterFeatureAction={handleAfterFeatureAction} portalTarget={stateFeaturesSlot} />
      </CapabilityGate>
    ) : null;

  if (isDesktop && isTagListMinimized) {
    return <>{featuresFilter}</>;
  }

  const handleFtueConfirmation = (selectedPreferences: FtuePreference[]) => {
    selectedPreferences.forEach((preference) => refineTag(preference));
    setLastTimeFilteredCharacters(Date.now());
  };

  return (
    <>
      {featuresFilter}
      {isFiltersV2 ? <FilterPanelCharacterV2 {...filterPanelProps} /> : <FilterPanelCharacterV1 {...filterPanelProps} />}
      <CapabilityFtueModal onConfirmation={handleFtueConfirmation} />
    </>
  );
};

export default React.memo(FilterBoxCharacter);
